import React from 'react';
import {
  Description as DocumentIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import { useMediaQuery, useTheme } from '@mui/material';
import {
  amber,
  blue,
  cyan,
  green,
  indigo,
  orange,
  pink,
  purple,
  red,
  teal
} from '@mui/material/colors';
import { alpha } from '@mui/system';
import {
  MdAdminPanelSettings,
  MdAssignment,
  MdCategory,
  MdFolder,
  MdPendingActions
} from 'react-icons/md';
import fieldTypes from '../SignatureFields';

// ✅ FIXED: Create a custom hook for responsive behavior
export const useResponsive = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  return { isMobile, theme };
};

// Predefined color palette for dynamic assignment (vibrant yet professional)
const colorPalette = [
  blue[600], // Vibrant blue
  green[600], // Rich green
  purple[500], // Soft purple
  teal[500], // Fresh teal
  indigo[500], // Deep indigo
  amber[600], // Warm amber
  pink[500], // Subtle pink
  cyan[500], // Bright cyan
  orange[600], // Energetic orange
  red[500] // Bold red
];

// Predefined icon set for dynamic assignment
const iconSet = [
  <MdAssignment key="assignment" />,
  <MdCategory key="category" />,
  <MdAdminPanelSettings key="admin" />,
  <MdPendingActions key="pending" />,
  <MdFolder key="folder" />
];

// Simple hash function to map strings to a number
const hashStringToIndex = (str: string | undefined, max: number): number => {
  if (!str) return 0;
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash) % max;
};

// Helper function to get a consistent color for any category name
export const getCategoryColor = (category: string | undefined): string => {
  if (!category) return blue[500]; // Fallback to a vibrant blue for undefined/empty categories
  return colorPalette[hashStringToIndex(category.toLowerCase().trim(), colorPalette.length)];
};

// Helper function to get a consistent icon for any category name
export const getCategoryIcon = (category: string | undefined): React.ReactElement => {
  if (!category) return <MdCategory key="default" />; // Fallback for undefined/empty category
  return iconSet[hashStringToIndex(category.toLowerCase().trim(), iconSet.length)];
};

// Typography styles
export const typography = {
  mainTitle: { fontSize: '1rem' },
  sectionTitle: { fontSize: '0.9rem' },
  cardTitle: { fontSize: '0.8rem' },
  bodyText: { fontSize: '0.7rem' },
  smallText: { fontSize: '0.65rem' },
  buttonText: { fontSize: '0.7rem' }
};

// Button styles
export const buttonStyles = {
  base: {
    textTransform: 'none' as const,
    fontWeight: 600,
    borderRadius: 1.5,
    boxShadow: 'none',
    fontSize: typography.buttonText.fontSize,
    '&:hover': {
      boxShadow: 'none'
    }
  },
  sizes: {
    small: {
      minWidth: 60,
      height: 28,
      px: 1.5,
      py: 0.25
    },
    medium: {
      minWidth: 80,
      height: 32,
      px: 2,
      py: 0.5
    }
  },
  variants: {
    contained: {
      bgcolor: 'primary.main',
      color: 'white',
      '&:hover': {
        bgcolor: 'primary.dark'
      }
    },
    outlined: {
      color: 'primary.main',
      borderColor: 'primary.main',
      '&:hover': {
        borderColor: 'primary.dark',
        bgcolor: alpha(blue[500], 0.08)
      }
    }
  }
};

// EXACT color scheme from AssignedInputFieldOverlay
export const getFieldColor = (type: string): string => {
  switch (type?.toLowerCase()) {
    case 'signature':
      return '#4f46e5'; // indigo-600
    case 'text':
    case 'name':
    case 'title':
      return '#374151'; // gray-700
    case 'date':
    case 'expiry':
      return '#6b7280'; // gray-500
    case 'email':
      return '#4b5563'; // gray-600
    default:
      return '#6b7280'; // gray-500
  }
};

export const getFieldConfig = (fieldKey: string) =>
  fieldTypes.find((f) => f.fieldKey === fieldKey) || fieldTypes[0];

export const getFieldsByCategory = () => {
  const grouped = fieldTypes.reduce((acc, field) => {
    if (!acc[field.category]) acc[field.category] = [];
    acc[field.category].push(field);
    return acc;
  }, {} as Record<string, typeof fieldTypes>);
  return grouped;
};

export const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });

export const getFileType = (file: File): 'pdf' | 'image' | 'document' => {
  if (file.type === 'application/pdf') return 'pdf';
  if (file.type.startsWith('image/')) return 'image';
  if (
    file.type === 'application/msword' ||
    file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ) {
    return 'document';
  }
  return 'document';
};

export const getFileExtension = (filename: string): string => {
  return filename.split('.').pop()?.toLowerCase() || '';
};

// ✅ FIXED: Remove hook usage and make it accept isMobile parameter
export const getFileIcon = (
  type: 'pdf' | 'image' | 'document',
  isMobile = false
): React.ReactElement => {
  const iconSize = isMobile ? 16 : 20;
  switch (type) {
    case 'pdf':
      return <PdfIcon sx={{ fontSize: iconSize, color: '#f44336' }} />;
    case 'image':
      return <ImageIcon sx={{ fontSize: iconSize, color: '#2196f3' }} />;
    case 'document':
      return <DocumentIcon sx={{ fontSize: iconSize, color: '#4caf50' }} />;
    default:
      return <DocumentIcon sx={{ fontSize: iconSize, color: '#9e9e9e' }} />;
  }
};

export const allowedMimeTypes = [
  'application/pdf',
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
];

export const allowedExtensions = ['pdf', 'doc', 'docx', 'png', 'jpg', 'jpeg', 'gif', 'webp'];

export const formatDate = (dateString: string) => {
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  } catch {
    return dateString;
  }
};


